import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type JournalSearchIndex,
  type TrustedJournalVerseText,
  type VisibleJournalSearchLabel,
} from '@/lib/journal-search';
import type { JournalEntry } from '@/lib/journal-aggregate';
import type { JournalSearchWorkerResponse } from '@/lib/journal-search-worker-protocol';

/**
 * An opaque caller-owned generation stamp. The page changes it whenever owner,
 * auth epoch, source corpus, or locale changes; the hook compares identity only.
 */
export type JournalSearchScope = Readonly<object>;

type IndexIdentity = {
  scope: JournalSearchScope;
  entries: readonly JournalEntry[];
  trustedVerseTexts: readonly TrustedJournalVerseText[];
  visibleLabels: readonly VisibleJournalSearchLabel[];
};

type BuiltIndex = IndexIdentity & {
  index: JournalSearchIndex;
};

export type JournalSearchIndexState =
  | { kind: 'idle'; index: null; retry: () => void }
  | { kind: 'building'; index: null; retry: () => void }
  | { kind: 'ready'; index: JournalSearchIndex; retry: () => void }
  | { kind: 'failed'; index: null; retry: () => void };

export interface JournalSearchIndexOptions {
  /** False keeps prior same-scope work cached but schedules no new work. */
  enabled: boolean;
  /** Owner/auth/corpus/locale generation supplied by the caller. */
  scope: JournalSearchScope;
}

/**
 * Builds the bounded index only after search is enabled, then reuses it across
 * query changes. Exact input + scope checks make an older owner/corpus/locale
 * build invisible synchronously, before effect cleanup gets a chance to run.
 */
export function useJournalSearchIndex(
  entries: readonly JournalEntry[],
  trustedVerseTexts: readonly TrustedJournalVerseText[],
  visibleLabels: readonly VisibleJournalSearchLabel[],
  { enabled, scope }: JournalSearchIndexOptions,
): JournalSearchIndexState {
  const [built, setBuilt] = useState<BuiltIndex | null>(null);
  const [failedIdentity, setFailedIdentity] = useState<IndexIdentity | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  const requestId = useRef(0);
  const retry = useCallback(() => {
    setFailedIdentity(null);
    setRetryTick((value) => value + 1);
  }, []);
  const current =
    built?.scope === scope &&
    built.entries === entries &&
    built.trustedVerseTexts === trustedVerseTexts &&
    built.visibleLabels === visibleLabels
      ? built
      : null;
  const failed =
    failedIdentity?.scope === scope &&
    failedIdentity.entries === entries &&
    failedIdentity.trustedVerseTexts === trustedVerseTexts &&
    failedIdentity.visibleLabels === visibleLabels;

  useEffect(() => {
    if (!enabled || current || failed) return;

    const thisRequest = ++requestId.current;
    let cancelled = false;
    let worker: Worker;
    try {
      worker = new Worker(
        new URL('../workers/journal-search-worker.ts', import.meta.url),
        { type: 'module', name: 'scripture-journal-search' },
      );
    } catch (error) {
      console.error('[journal] search worker unavailable', error);
      queueMicrotask(() => {
        if (cancelled || requestId.current !== thisRequest) return;
        setBuilt(null);
        setFailedIdentity({ scope, entries, trustedVerseTexts, visibleLabels });
      });
      return () => {
        cancelled = true;
      };
    }
    worker.onmessage = (event: MessageEvent<JournalSearchWorkerResponse>) => {
      const response = event.data;
      if (
        cancelled ||
        requestId.current !== thisRequest ||
        response.requestId !== thisRequest
      ) {
        worker.terminate();
        return;
      }
      if (response.ok) {
        worker.terminate();
        setFailedIdentity(null);
        setBuilt({
          scope,
          entries,
          trustedVerseTexts,
          visibleLabels,
          index: response.index,
        });
        return;
      }
      worker.terminate();
      console.error('[journal] search index build failed', response.error);
      setBuilt(null);
      setFailedIdentity({ scope, entries, trustedVerseTexts, visibleLabels });
    };
    worker.onerror = (event) => {
      console.error('[journal] search worker failed', event.message);
      if (cancelled || requestId.current !== thisRequest) return;
      worker.terminate();
      setBuilt(null);
      setFailedIdentity({ scope, entries, trustedVerseTexts, visibleLabels });
    };
    worker.postMessage({
      requestId: thisRequest,
      entries,
      trustedVerseTexts,
      visibleLabels,
    });
    return () => {
      cancelled = true;
      worker.terminate();
    };
  }, [
    current,
    enabled,
    entries,
    failed,
    retryTick,
    scope,
    trustedVerseTexts,
    visibleLabels,
  ]);

  if (!enabled) return { kind: 'idle', index: null, retry };
  if (current) return { kind: 'ready', index: current.index, retry };
  if (failed) return { kind: 'failed', index: null, retry };
  return { kind: 'building', index: null, retry };
}
